import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Plus, Trash2, RefreshCw, Repeat,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { cn }              from '@/utils/cn';
import { apiClient }       from '@/api/client';
import { AccountLookupField } from '@/components/erp/AccountLookupField';
import { currencyApi }     from '@/api/currency.api';
import type { CurrencyListDto } from '@/types/currency.types';

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface RecurringJournalLineDto {
  id?: string;
  /** Resolved from accountCode on save — the API posts against the id, not the code the user typed. */
  accountId?: string;
  accountCode: string;
  accountName?: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
  costCenterId?: string | null;
  locationId?: string | null;
}

interface RecurringJournalFormDto {
  code: string;
  description: string;
  frequencyType: string;
  interval: number;
  startDate: string;
  endDate?: string;
  currencyCode: string;
  autoSubmit: boolean;
  autoPost: boolean;
  narration?: string;
  lines: RecurringJournalLineDto[];
}

const FREQUENCY_TYPES = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'];

const emptyLine = (): RecurringJournalLineDto => ({
  accountCode:   '',
  description:   '',
  debitAmount:   0,
  creditAmount:  0,
  costCenterId:  null,
  locationId:    null,
});

const defaultForm = (): RecurringJournalFormDto => ({
  code:          '',
  description:   '',
  frequencyType: 'Monthly',
  interval:      1,
  startDate:     '',
  endDate:       '',
  currencyCode:  'USD',
  autoSubmit:    false,
  autoPost:      false,
  narration:     '',
  lines:         [emptyLine()],
});

// â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const LabeledField = ({
  label, required, children,
}: { label: string; required?: boolean; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = cn(
  'w-full text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none'
);

// â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const RecurringJournalFormPage = () => {
  const { id }            = useParams<{ id: string }>();
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const isEdit            = Boolean(id);

  const canSave = isEdit
    ? hasPermission(Permissions.RECURRING_JOURNAL_EDIT)
    : hasPermission(Permissions.RECURRING_JOURNAL_CREATE);

  const [form,    setForm]    = useState<RecurringJournalFormDto>(defaultForm());
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [errors,  setErrors]  = useState<Partial<Record<keyof RecurringJournalFormDto, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [currencies, setCurrencies] = useState<CurrencyListDto[]>([]);

  // Active currencies only — a template must not be pinned to a currency that has been retired (QA #1).
  useEffect(() => {
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(res => setCurrencies(res.data.data?.items ?? []))
      .catch(() => { /* falls back to a free code below */ });
  }, []);

  // â”€â”€ Load existing record â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const loadRecord = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      // The API lives at /recurring-journals, not /finance/recurring-journals: this page was asking a
      // URL that does not exist, so loading, creating and editing all 404'd in silence. And what it
      // returns is not shaped like this form — it speaks recurringCode, frequencyInterval and
      // accountId where the form speaks code, interval and accountCode. Translate, rather than
      // assigning the response straight into form state and hoping.
      const res = await apiClient.get(`/recurring-journals/${id}`);
      const d = res.data?.data;
      if (!d) return;
      setForm({
        code:          d.recurringCode ?? '',
        description:   d.description ?? '',
        frequencyType: d.frequencyType ?? 'Monthly',
        interval:      d.frequencyInterval ?? 1,
        startDate:     String(d.startDate ?? '').slice(0, 10),
        endDate:       d.endDate ? String(d.endDate).slice(0, 10) : undefined,
        currencyCode:  d.currencyCode ?? 'LKR',
        autoSubmit:    !!d.autoSubmit,
        autoPost:      !!d.autoPost,
        narration:     d.narrationTemplate ?? '',
        lines: (d.lines ?? []).map((l: any) => ({
          id:           l.id,
          accountId:    l.accountId ?? '',
          accountCode:  l.accountCode ?? '',
          accountName:  l.accountName ?? '',
          description:  l.description ?? '',
          debitAmount:  l.debitAmount ?? 0,
          creditAmount: l.creditAmount ?? 0,
          costCenterId: l.costCenterId ?? null,
          locationId:   l.locationId ?? null,
        })),
      });
    } catch (err) {
      console.error('Failed to load recurring journal', err);
      setApiError('Failed to load this recurring journal.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (isEdit) loadRecord(); }, [isEdit, loadRecord]);

  // â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const setField = <K extends keyof RecurringJournalFormDto>(
    key: K, value: RecurringJournalFormDto[K]
  ) => setForm(f => ({ ...f, [key]: value }));

  const setLine = (idx: number, key: keyof RecurringJournalLineDto, value: string | number) => {
    setForm(f => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [key]: value };
      // A line is a debit or a credit, never both (rule F7) — entering one clears the other.
      if (key === 'debitAmount'  && Number(value) > 0) lines[idx].creditAmount = 0;
      if (key === 'creditAmount' && Number(value) > 0) lines[idx].debitAmount  = 0;
      return { ...f, lines };
    });
  };

  // Account is picked from a searchable lookup, so the id, code and name arrive together (QA #21) —
  // no more typing a code by heart and hoping it resolves at save time.
  const setLineAccount = (idx: number, accountId: string | null, code: string | null, name: string | null) => {
    setForm(f => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], accountId: accountId ?? undefined, accountCode: code ?? '', accountName: name ?? '' };
      return { ...f, lines };
    });
  };

  const addLine    = ()      => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (idx: number) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  // â”€â”€ Validation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const validate = (): boolean => {
    const errs: typeof errors = {};
    if (!form.code.trim())        errs.code        = 'Code is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    if (!form.startDate)          errs.startDate   = 'Start date is required';
    if (form.interval < 1)        errs.interval    = 'Interval must be at least 1';
    if (form.lines.length === 0)  errs.lines       = 'At least one line is required' as any;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // â”€â”€ Submit â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleSave = async () => {
    if (!validate()) return;
    setApiError(null);
    setSaving(true);
    try {
      // The user types an account CODE; the API posts against an account ID. Resolve every line before
      // sending, and refuse to send a line whose code matches no account — the alternative is a
      // template that saves happily and then fails every month at run time, for a reason nobody
      // recorded.
      const lines = [];
      for (const l of form.lines) {
        let accountId = l.accountId;
        if (!accountId && l.accountCode.trim()) {
          try {
            const r = await apiClient.get(`/accounts/by-code/${encodeURIComponent(l.accountCode.trim())}`);
            accountId = r.data?.data?.id;
          } catch { /* falls through to the check below */ }
        }
        if (!accountId) {
          setApiError(`No account has the code "${l.accountCode}".`);
          setSaving(false);
          return;
        }
        lines.push({
          accountId,
          accountCode:  l.accountCode.trim(),
          accountName:  l.accountName ?? '',
          costCenterId: l.costCenterId ?? null,
          locationId:   l.locationId ?? null,
          debitAmount:  Number(l.debitAmount)  || 0,
          creditAmount: Number(l.creditAmount) || 0,
          description:  l.description || null,
        });
      }

      if (isEdit) {
        await apiClient.put(`/recurring-journals/${id}`, {
          description:        form.description.trim(),
          endDate:            form.endDate || null,
          autoSubmit:         form.autoSubmit,
          autoPost:           form.autoPost,
          isWorkflowRequired: false,
          narrationTemplate:  form.narration || null,
          lines,
        });
      } else {
        await apiClient.post('/recurring-journals', {
          locationId:         null,
          costCenterId:       null,
          financialYearCode:  'FY2026',
          recurringCode:      form.code.trim(),
          description:        form.description.trim(),
          frequencyType:      form.frequencyType,
          frequencyInterval:  Number(form.interval) || 1,
          startDate:          form.startDate,
          endDate:            form.endDate || null,
          currencyCode:       form.currencyCode,
          exchangeRate:       1,
          autoSubmit:         form.autoSubmit,
          autoPost:           form.autoPost,
          isWorkflowRequired: false,
          narrationTemplate:  form.narration || null,
          lines,
        });
      }
      navigate(AppRoutes.RECURRING_JOURNALS_LIST);
    } catch (err: any) {
      setApiError(err?.response?.data?.message ?? 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // â”€â”€ Totals â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const totalDebits  = form.lines.reduce((s, l) => s + (Number(l.debitAmount)  || 0), 0);
  const totalCredits = form.lines.reduce((s, l) => s + (Number(l.creditAmount) || 0), 0);
  const isBalanced   = Math.abs(totalDebits - totalCredits) < 0.005;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(AppRoutes.RECURRING_JOURNALS_LIST)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Repeat size={20} className="text-teal-600 dark:text-teal-400" />
                {isEdit ? 'Edit Recurring Journal' : 'New Recurring Journal'}
              </h1>
              {isEdit && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{form.code}</p>
              )}
            </div>
          </div>
          {canSave && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              {isEdit ? 'Save Changes' : 'Create Template'}
            </button>
          )}
        </div>
      </div>

      {/* â”€â”€ Form body â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex-1 overflow-auto p-6 space-y-6">

        {apiError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {apiError}
          </div>
        )}

        {/* â”€â”€ Basic info â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Template Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <LabeledField label="Code" required>
              <input value={form.code} onChange={e => setField('code', e.target.value.toUpperCase())}
                placeholder="e.g. REC-RENT-001"
                className={cn(inputCls, errors.code && 'border-red-400 ring-1 ring-red-400')} />
              {errors.code && <p className="text-xs text-red-500 mt-1">{errors.code}</p>}
            </LabeledField>

            <LabeledField label="Description" required>
              <input value={form.description} onChange={e => setField('description', e.target.value)}
                placeholder="e.g. Monthly Office Rent"
                className={cn(inputCls, errors.description && 'border-red-400 ring-1 ring-red-400')} />
              {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
            </LabeledField>

            <LabeledField label="Currency">
              <select value={form.currencyCode} onChange={e => setField('currencyCode', e.target.value)}
                className={inputCls}>
                {/* Keep the loaded value selectable even if it is now inactive (editing an old template). */}
                {form.currencyCode && !currencies.some(c => c.code === form.currencyCode) && (
                  <option value={form.currencyCode}>{form.currencyCode}</option>
                )}
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
            </LabeledField>

            <LabeledField label="Frequency Type" required>
              <select value={form.frequencyType} onChange={e => setField('frequencyType', e.target.value)}
                className={inputCls}>
                {FREQUENCY_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </LabeledField>

            <LabeledField label="Interval" required>
              <input type="number" min={1} value={form.interval}
                onChange={e => setField('interval', parseInt(e.target.value) || 1)}
                className={cn(inputCls, errors.interval && 'border-red-400 ring-1 ring-red-400')} />
              {errors.interval && <p className="text-xs text-red-500 mt-1">{errors.interval}</p>}
            </LabeledField>

            <LabeledField label="Start Date" required>
              <input type="date" value={form.startDate} onChange={e => setField('startDate', e.target.value)}
                className={cn(inputCls, errors.startDate && 'border-red-400 ring-1 ring-red-400')} />
              {errors.startDate && <p className="text-xs text-red-500 mt-1">{errors.startDate}</p>}
            </LabeledField>

            <LabeledField label="End Date">
              <input type="date" value={form.endDate ?? ''} onChange={e => setField('endDate', e.target.value || undefined)}
                className={inputCls} />
            </LabeledField>
          </div>

          <div className="mt-4">
            <LabeledField label="Narration / Description">
              <textarea value={form.narration ?? ''} onChange={e => setField('narration', e.target.value)}
                rows={2} placeholder="Optional journal narration..."
                className={cn(inputCls, 'resize-none')} />
            </LabeledField>
          </div>
        </div>

        {/* â”€â”€ Posting settings â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Posting Settings</h2>
          <div className="flex items-center gap-8">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.autoSubmit} onChange={e => setField('autoSubmit', e.target.checked)}
                className="w-4 h-4 text-teal-600 dark:text-teal-400 rounded border-slate-300 dark:border-gray-600 focus:ring-teal-500" />
              <span className="text-sm text-slate-700 dark:text-slate-300">Auto Submit for Approval</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.autoPost} onChange={e => setField('autoPost', e.target.checked)}
                className="w-4 h-4 text-teal-600 dark:text-teal-400 rounded border-slate-300 dark:border-gray-600 focus:ring-teal-500" />
              <span className="text-sm text-slate-700 dark:text-slate-300">Auto Post on Approval</span>
            </label>
          </div>
        </div>

        {/* â”€â”€ Journal lines â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Journal Lines</h2>
              <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                Totals: Dr {totalDebits.toLocaleString('en-US', { minimumFractionDigits: 2 })} · Cr {totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                {' '}
                {isBalanced
                  ? <span className="text-green-600 dark:text-green-400 font-medium">· Balanced</span>
                  : <span className="text-red-500 font-medium">· Out of balance by {Math.abs(totalDebits - totalCredits).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                }
              </p>
            </div>
            {canSave && (
              <button onClick={addLine}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-teal-300 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors">
                <Plus size={14} /> Add Line
              </button>
            )}
          </div>

          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider w-8">#</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Account Code</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Debit</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Credit</th>
                  {canSave && <th className="px-3 py-2.5 w-8" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {form.lines.map((line, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-slate-500 dark:text-gray-400 text-xs text-center">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <div className="w-64">
                        <AccountLookupField
                          label=""
                          value={line.accountId ?? null}
                          displayCode={line.accountCode}
                          displayName={line.accountName}
                          isPostingAccount
                          placeholder="Search account…"
                          onChange={(accId, code, name) => setLineAccount(idx, accId, code, name)}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <input value={line.description}
                        onChange={e => setLine(idx, 'description', e.target.value)}
                        placeholder="Line description..."
                        className="w-52 text-xs border border-slate-200 dark:border-slate-600 rounded px-2 py-1.5
                                   bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" min={0} step="0.01" value={line.debitAmount || ''}
                        onChange={e => setLine(idx, 'debitAmount', parseFloat(e.target.value) || 0)}
                        className="w-28 text-xs text-right border border-slate-200 dark:border-slate-600 rounded px-2 py-1.5
                                   bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none font-mono" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" min={0} step="0.01" value={line.creditAmount || ''}
                        onChange={e => setLine(idx, 'creditAmount', parseFloat(e.target.value) || 0)}
                        className="w-28 text-xs text-right border border-slate-200 dark:border-slate-600 rounded px-2 py-1.5
                                   bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none font-mono" />
                    </td>
                    {canSave && (
                      <td className="px-3 py-2">
                        {form.lines.length > 1 && (
                          <button onClick={() => removeLine(idx)}
                            className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/60">
                <tr>
                  <td colSpan={3} className="px-3 py-2.5 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                    Totals
                  </td>
                  <td className={cn('px-3 py-2.5 text-right font-mono font-bold text-sm', isBalanced ? 'text-teal-700 dark:text-teal-400' : 'text-red-600 dark:text-red-400')}>
                    {totalDebits.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className={cn('px-3 py-2.5 text-right font-mono font-bold text-sm', isBalanced ? 'text-teal-700 dark:text-teal-400' : 'text-red-600 dark:text-red-400')}>
                    {totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td colSpan={canSave ? 3 : 2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

